import { useMemo, useState, type MouseEvent } from "react";
import {
  alpha,
  Box,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { InsightSelectableBarList } from "./InsightSelectableBarList";
import {
  READABLE_BODY_SX,
  READABLE_SECONDARY_SX,
  READABLE_TITLE_SX,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import type { InsightChartItem } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

const TOP_COUNT = 5;

type ListScope = "top" | "all";

interface InsightRankingPanelProps {
  sectionTitle: string;
  insightText?: string;
  searchPlaceholder: string;
  rankingScopeAriaLabel: string;
  items: InsightChartItem[];
  selectedKeys: string[];
  accentColor: string;
  patientCountByKey: Record<string, number>;
  itemUnitLabel: string;
  onToggle: (key: string) => void;
  disabled?: boolean;
}

export function InsightRankingPanel({
  sectionTitle,
  insightText,
  searchPlaceholder,
  rankingScopeAriaLabel,
  items,
  selectedKeys,
  accentColor,
  patientCountByKey,
  itemUnitLabel,
  onToggle,
  disabled = false,
}: InsightRankingPanelProps) {
  const [scope, setScope] = useState<ListScope>("top");
  const [search, setSearch] = useState("");

  const showScopeToggle = items.length > TOP_COUNT;
  const showAll = scope === "all";

  const totalCount = items.reduce((acc, item) => acc + item.value, 0);
  const maxValue = Math.max(...items.map((item) => item.value), 1);
  const topFive = useMemo(() => items.slice(0, TOP_COUNT), [items]);

  const filteredAll = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [items, search]);

  const listItems = showAll ? filteredAll : topFive;

  const handleScopeChange = (_: MouseEvent<HTMLElement>, next: ListScope | null) => {
    if (!next) return;
    setScope(next);
    if (next === "top") setSearch("");
  };

  const toggleSx = {
    flex: 1,
    ...READABLE_BODY_SX,
    fontWeight: 600,
    textTransform: "none",
    py: 1.35,
    px: 1,
    lineHeight: 1.3,
    "&.Mui-selected": {
      bgcolor: alpha(accentColor, 0.14),
      color: accentColor,
      fontWeight: 700,
      borderColor: alpha(accentColor, 0.45),
      "&:hover": {
        bgcolor: alpha(accentColor, 0.2),
      },
    },
  } as const;

  return (
    <Box>
      <Stack spacing={1.25} sx={{ mb: 1.25 }}>
        <Typography fontWeight={700} sx={READABLE_TITLE_SX}>
          {sectionTitle}
        </Typography>

        {showScopeToggle ? (
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={scope}
            onChange={handleScopeChange}
            disabled={disabled}
            aria-label={rankingScopeAriaLabel}
            sx={{
              "& .MuiToggleButtonGroup-grouped": {
                borderColor: alpha(accentColor, 0.35),
              },
            }}
          >
            <ToggleButton value="top" sx={toggleSx}>
              Top 5
            </ToggleButton>
            <ToggleButton value="all" sx={toggleSx}>
              Todas ({items.length})
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
      </Stack>

      {insightText ? (
        <Typography sx={{ ...READABLE_SECONDARY_SX, mb: 1.25 }}>{insightText}</Typography>
      ) : null}

      {showAll ? (
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          sx={{ mb: 1.5, ...READABLE_BODY_SX }}
          InputProps={{
            sx: READABLE_BODY_SX,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 22 }} />
              </InputAdornment>
            ),
          }}
        />
      ) : null}

      <InsightSelectableBarList
        items={listItems}
        selectedKeys={selectedKeys}
        accentColor={accentColor}
        totalForPercent={totalCount}
        maxValue={maxValue}
        patientCountByKey={patientCountByKey}
        itemUnitLabel={itemUnitLabel}
        onToggle={onToggle}
        disabled={disabled}
      />
    </Box>
  );
}
